import { useEffect, useRef } from 'react'

import { pageActionsChanged, registerPageAction } from '@aytm/lib/kbar/page-actions'
import { holdScrollAnchor } from '@aytm/app/stats/lib/page/scroll-anchor'
import {
  allCollapsed,
  collapseParams,
  collapsibleQuestions,
  questionLogicParams
} from '@aytm/app/stats/page/sidebar/view-options/lib/params'

// The keyboard shortcuts for the view options' "Collapse all questions" and "Hide aytm Logic" rows.
// The sidebar mounts only the open panel, so a shortcut that clicked those checkboxes did nothing
// from any other panel; this acts on the report the same way the rows do, from wherever the reader is.
// Registered once and read through a ref, so a shortcut always sees the current report.
const useViewOptionsShortcuts = ({ survey, model, report, features, onParamsChange }) => {
  const latest = useRef(null)
  latest.current = { survey, model, report, features, onParamsChange }

  useEffect(() => {
    const viewOptionsOn = () => !latest.current.features.view_options?.hidden

    const unregisterCollapse = registerPageAction('collapse-expand-results', {
      perform: () => {
        const { survey, report, features, onParamsChange } = latest.current
        if (!viewOptionsOn() || features.toggling?.disabled || !report?.current) return

        const questions = collapsibleQuestions(survey, report.current)
        const collapsed = !allCollapsed(questions, report.current.params)
        if (collapsed) window.scrollTo(0, 0)
        onParamsChange(collapseParams(questions, collapsed), false)
      }
    })

    const logicAvailable = () =>
      Boolean(latest.current.survey?.cl_exists) && viewOptionsOn() && !latest.current.features.view_options?.disabled

    const unregisterLogic = registerPageAction('hide-aytm-logic', {
      isAvailable: logicAvailable,
      perform: () => {
        if (!logicAvailable()) return

        // every question title is rewritten at a new height, so the reader is held where they are
        holdScrollAnchor()
        latest.current.onParamsChange(questionLogicParams(latest.current.model), false)
      }
    })

    return () => {
      unregisterCollapse()
      unregisterLogic()
    }
  }, [])

  // the survey's logic usually lands after the sidebar has registered, so the bar is told again
  useEffect(
    () => pageActionsChanged(),
    [survey?.cl_exists, features.view_options?.hidden, features.view_options?.disabled]
  )
}

export default useViewOptionsShortcuts
